<template>
  <Dialog
    v-model:visible="isVisible"
    :header="t('googleFonts.modalTitle')"
    :modal="true"
    :closable="true"
    :draggable="false"
    class="google-font-picker-modal"
    :style="{ width: '70vw', maxWidth: '1000px' }"
    @hide="handleClose"
  >
    <template #header>
      <div class="flex align-items-center gap-2">
        <i class="fas fa-font" />
        <span>{{ t('googleFonts.modalTitle') }}</span>
      </div>
    </template>

    <!-- Warning for email templates -->
    <InlineMessage
      v-if="isEmailTemplate"
      severity="warn"
      class="mb-3 w-full"
    >
      <div class="flex flex-column gap-1">
        <strong>{{ t('googleFonts.emailWarningTitle') }}</strong>
        <span>{{ t('googleFonts.emailWarningMessage') }}</span>
      </div>
    </InlineMessage>

    <!-- Search and filters -->
    <div class="google-font-picker-toolbar mb-3">
      <div class="flex gap-2 align-items-center flex-wrap">
        <!-- Search input -->
        <span class="p-input-icon-left flex-grow-1" style="min-width: 200px;">
          <i class="fas fa-search" />
          <InputText
            v-model="searchQuery"
            :placeholder="t('googleFonts.searchPlaceholder')"
            class="w-full"
            @input="handleSearch"
          />
        </span>

        <!-- Category filter -->
        <Dropdown
          v-model="selectedCategory"
          :options="categoryOptions"
          option-label="label"
          option-value="id"
          :placeholder="t('googleFonts.categoryPlaceholder')"
          class="category-dropdown"
          style="min-width: 180px;"
        >
          <template #option="slotProps">
            <div class="flex align-items-center gap-2">
              <i :class="slotProps.option.icon" />
              <span>{{ t(slotProps.option.label) }}</span>
            </div>
          </template>
          <template #value="slotProps">
            <div v-if="slotProps.value" class="flex align-items-center gap-2">
              <i :class="getCategoryIcon(slotProps.value)" />
              <span>{{ getCategoryLabel(slotProps.value) }}</span>
            </div>
          </template>
        </Dropdown>
      </div>

      <!-- Tabs -->
      <div class="google-font-tabs mt-3">
        <div class="flex gap-1">
          <Button
            :label="t('googleFonts.tabs.all')"
            :class="['p-button-sm', activeTab === 'all' ? '' : 'p-button-outlined']"
            @click="activeTab = 'all'"
          />
          <Button
            :label="t('googleFonts.tabs.favorites')"
            icon="fas fa-star"
            :class="['p-button-sm', activeTab === 'favorites' ? '' : 'p-button-outlined']"
            :badge="favorites.length > 0 ? String(favorites.length) : undefined"
            @click="activeTab = 'favorites'"
          />
          <Button
            :label="t('googleFonts.tabs.recent')"
            icon="fas fa-history"
            :class="['p-button-sm', activeTab === 'recent' ? '' : 'p-button-outlined']"
            @click="activeTab = 'recent'"
          />
          <Button
            v-if="isEmailTemplate"
            :label="t('googleFonts.tabs.emailSafe')"
            icon="fas fa-envelope-open-text"
            :class="['p-button-sm', activeTab === 'emailSafe' ? '' : 'p-button-outlined']"
            @click="activeTab = 'emailSafe'"
          />
        </div>
      </div>
    </div>

    <!-- Preview text input -->
    <div class="preview-text-section mb-3">
      <label class="block text-sm text-600 mb-1">{{ t('googleFonts.previewText') }}</label>
      <InputText
        v-model="previewText"
        :placeholder="t('googleFonts.previewTextPlaceholder')"
        class="w-full"
      />
    </div>

    <!-- Fonts grid -->
    <div class="google-fonts-grid-container">
      <!-- Loading state -->
      <div v-if="isLoading" class="flex justify-content-center align-items-center p-5">
        <ProgressSpinner style="width: 50px; height: 50px;" />
      </div>

      <!-- Empty state -->
      <div
        v-else-if="displayedFonts.length === 0"
        class="flex flex-column align-items-center justify-content-center p-5 text-600"
      >
        <i class="fas fa-search fa-3x mb-3" />
        <span>{{ t('googleFonts.noResults') }}</span>
      </div>

      <!-- Fonts grid -->
      <div v-else class="google-fonts-grid">
        <div
          v-for="font in displayedFonts"
          :key="font.family"
          class="font-card"
          :class="{ 'font-card-selected': selectedFont === font.family }"
          @click="selectFont(font)"
        >
          <!-- Font header -->
          <div class="font-card-header">
            <span class="font-name">{{ font.family }}</span>
            <div class="font-actions">
              <Button
                :icon="isFavorite(font.family) ? 'fas fa-star' : 'far fa-star'"
                class="p-button-text p-button-sm p-button-rounded favorite-btn"
                :class="{ 'favorite-active': isFavorite(font.family) }"
                @click.stop="toggleFavorite(font.family)"
              />
              <span
                v-if="isEmailSafeFont(font.family)"
                v-tooltip.top="t('googleFonts.emailSafeTooltip')"
                class="email-safe-badge"
              >
                <i class="fas fa-envelope-open-text" />
              </span>
            </div>
          </div>

          <!-- Font preview -->
          <div
            class="font-preview"
            :style="getFontPreviewStyle(font)"
            @mouseenter="loadFontOnHover(font)"
          >
            {{ previewText || t('googleFonts.defaultPreviewText') }}
          </div>

          <!-- Font category -->
          <div class="font-category">
            {{ font.category }}
          </div>
        </div>
      </div>

      <!-- Load more button -->
      <div
        v-if="hasMoreFonts"
        class="flex justify-content-center mt-3"
      >
        <Button
          :label="t('googleFonts.loadMore')"
          icon="fas fa-plus"
          class="p-button-outlined"
          @click="loadMoreFonts"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex justify-content-between align-items-center w-full">
        <div class="flex align-items-center gap-2 text-sm text-600">
          <i class="fas fa-info-circle" />
          <span v-if="selectedFont">
            {{ t('googleFonts.selectedFont') }}: <strong>{{ selectedFont }}</strong>
          </span>
          <span v-else>{{ t('googleFonts.selectFontHint') }}</span>
        </div>
        <div class="flex gap-2">
          <Button
            :label="t('googleFonts.cancel')"
            icon="fas fa-times"
            class="p-button-secondary"
            @click="handleCancel"
          />
          <Button
            :label="t('googleFonts.select')"
            icon="fas fa-check"
            class="p-button-success"
            :disabled="!selectedFont"
            @click="handleSelect"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
import InlineMessage from 'primevue/inlinemessage';
import ProgressSpinner from 'primevue/progressspinner';
import Tooltip from 'primevue/tooltip';

import { useGoogleFonts } from '@/composables/google-fonts/useGoogleFonts';
import { GoogleFont, EMAIL_SAFE_FONTS, FONT_CATEGORIES } from '@/composables/google-fonts/GoogleFontsService';

const FONTS_PER_PAGE = 30;

export default defineComponent({
  name: 'GoogleFontPickerModal',

  components: {
    Dialog,
    Button,
    InputText,
    Dropdown,
    InlineMessage,
    ProgressSpinner,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    currentFont: {
      type: String,
      default: '',
    },
    isEmailTemplate: {
      type: Boolean,
      default: false,
    },
    iframeDocument: {
      type: Object as PropType<Document | null>,
      default: null,
    },
  },

  emits: ['update:visible', 'select', 'cancel'],

  setup(props, { emit }) {
    const { t } = useI18n();

    // Google Fonts composable
    const {
      fonts,
      isLoading,
      filteredFonts,
      favoriteFonts,
      recentFontsList,
      emailSafeFonts,
      categories,
      favorites,
      loadFonts,
      addToFavorites,
      removeFromFavorites,
      isFavorite,
      addToRecent,
      injectFontToIframe,
      generateFontValue,
      isEmailSafe,
    } = useGoogleFonts();

    // Local state
    const searchQuery = ref('');
    const selectedCategory = ref('all');
    // Default to emailSafe tab for email templates
    const activeTab = ref(props.isEmailTemplate ? 'emailSafe' : 'all');
    const selectedFont = ref('');
    const previewText = ref('');
    const displayLimit = ref(FONTS_PER_PAGE);

    // Visibility control
    const isVisible = computed({
      get: () => props.visible,
      set: (value) => emit('update:visible', value),
    });

    // Category options for dropdown
    const categoryOptions = computed(() => FONT_CATEGORIES);

    // Get category icon
    const getCategoryIcon = (categoryId: string): string => {
      const category = FONT_CATEGORIES.find((c) => c.id === categoryId);
      return category?.icon || 'fas fa-font';
    };

    // Get category label
    const getCategoryLabel = (categoryId: string): string => {
      const category = FONT_CATEGORIES.find((c) => c.id === categoryId);
      return category ? t(category.label) : categoryId;
    };

    // Displayed fonts based on active tab and filters
    const displayedFonts = computed(() => {
      let result: GoogleFont[] = [];

      switch (activeTab.value) {
        case 'favorites':
          result = favoriteFonts.value;
          break;
        case 'recent':
          result = recentFontsList.value;
          break;
        case 'emailSafe':
          // Convert email safe fonts to GoogleFont format
          result = EMAIL_SAFE_FONTS.map((f) => ({
            family: f.family,
            category: f.category,
            variants: ['regular', 'bold'],
            subsets: ['latin'],
            version: '',
            lastModified: '',
            files: {},
          }));
          break;
        default:
          result = filteredFonts.value;
      }

      // Apply search filter
      if (searchQuery.value.trim()) {
        const query = searchQuery.value.toLowerCase();
        result = result.filter((font) => font.family.toLowerCase().includes(query));
      }

      // Apply category filter (except for emailSafe tab)
      if (selectedCategory.value !== 'all' && activeTab.value !== 'emailSafe') {
        result = result.filter((font) => font.category === selectedCategory.value);
      }

      // Limit displayed fonts
      return result.slice(0, displayLimit.value);
    });

    // Check if there are more fonts to load
    const hasMoreFonts = computed(() => {
      if (activeTab.value === 'favorites') {
        return displayLimit.value < favoriteFonts.value.length;
      }
      if (activeTab.value === 'recent') {
        return displayLimit.value < recentFontsList.value.length;
      }
      if (activeTab.value === 'emailSafe') {
        return false;
      }
      return displayLimit.value < filteredFonts.value.length;
    });

    // Load more fonts
    const loadMoreFonts = (): void => {
      displayLimit.value += FONTS_PER_PAGE;
    };

    // Handle search input
    const handleSearch = (): void => {
      displayLimit.value = FONTS_PER_PAGE;
    };

    // Select a font
    const selectFont = (font: GoogleFont): void => {
      selectedFont.value = font.family;

      // Inject font for preview if iframe is provided
      if (props.iframeDocument) {
        injectFontToIframe(font.family, props.iframeDocument);
      }
    };

    // Toggle favorite
    const toggleFavorite = (fontFamily: string): void => {
      if (isFavorite(fontFamily)) {
        removeFromFavorites(fontFamily);
      } else {
        addToFavorites(fontFamily);
      }
    };

    // Check if email safe
    const isEmailSafeFont = (fontFamily: string): boolean => isEmailSafe(fontFamily);

    // Track loaded fonts (using object for reactivity)
    const loadedFontsMap = ref<Record<string, boolean>>({});

    // Load font for preview - inject link and let browser handle it
    const loadFontForPreview = (fontFamily: string): void => {
      if (loadedFontsMap.value[fontFamily] || isEmailSafe(fontFamily)) {
        return;
      }

      // Mark as loading
      loadedFontsMap.value[fontFamily] = true;

      // Check if already in DOM
      const existingLink = document.querySelector(`link[data-font="${fontFamily}"]`);
      if (existingLink) {
        return;
      }

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.setAttribute('data-font', fontFamily);
      link.href = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(/ /g, '+')}:wght@400;700&display=swap`;
      document.head.appendChild(link);
    };

    // Load font on hover for better UX
    const loadFontOnHover = (font: GoogleFont): void => {
      loadFontForPreview(font.family);
    };

    // Get font preview style - always use the font name, browser will use fallback until loaded
    const getFontPreviewStyle = (font: GoogleFont): Record<string, string> => {
      // Load font if not already loading
      if (!loadedFontsMap.value[font.family] && !isEmailSafe(font.family)) {
        loadFontForPreview(font.family);
      }

      // For email-safe fonts
      if (isEmailSafe(font.family)) {
        return {
          fontFamily: generateFontValue(font.family, true),
        };
      }

      // Always return the font name - browser will apply when loaded
      return {
        fontFamily: `"${font.family}", ${font.category}`,
      };
    };

    // Handle select
    const handleSelect = (): void => {
      if (selectedFont.value) {
        addToRecent(selectedFont.value);

        const fontValue = generateFontValue(selectedFont.value);
        const fontData = {
          family: selectedFont.value,
          value: fontValue,
          isEmailSafe: isEmailSafe(selectedFont.value),
        };

        emit('select', fontData);
        isVisible.value = false;
      }
    };

    // Handle cancel
    const handleCancel = (): void => {
      emit('cancel');
      isVisible.value = false;
    };

    // Handle close
    const handleClose = (): void => {
      emit('cancel');
    };

    // Watch visibility to load fonts and reset state
    watch(() => props.visible, async (newVisible) => {
      if (newVisible) {
        // Reset state
        displayLimit.value = FONTS_PER_PAGE;
        selectedFont.value = props.currentFont || '';
        activeTab.value = 'all';

        // Load fonts if not already loaded
        if (fonts.value.length === 0) {
          await loadFonts();
        }

        // Preload fonts for preview
        preloadFontsForPreview();
      }
    });

    // Preload first batch of fonts for preview
    const preloadFontsForPreview = (): void => {
      const fontsToPreload = filteredFonts.value.slice(0, 30);
      fontsToPreload.forEach((font) => {
        loadFontForPreview(font.family);
      });
    };

    // Load fonts on mount if visible
    onMounted(() => {
      if (props.visible && fonts.value.length === 0) {
        loadFonts();
      }
    });

    return {
      t,
      isVisible,
      isLoading,
      searchQuery,
      selectedCategory,
      activeTab,
      selectedFont,
      previewText,
      displayedFonts,
      hasMoreFonts,
      categoryOptions,
      favorites,
      getCategoryIcon,
      getCategoryLabel,
      loadMoreFonts,
      handleSearch,
      selectFont,
      toggleFavorite,
      isFavorite,
      isEmailSafeFont,
      getFontPreviewStyle,
      loadFontOnHover,
      handleSelect,
      handleCancel,
      handleClose,
    };
  },
});
</script>

<style lang="scss">
.google-font-picker-modal {
  .p-dialog-content {
    max-height: 70vh;
    overflow-y: auto;
  }

  .p-dialog-footer {
    border-top: 1px solid #e9ecef;
  }
}
</style>

<style lang="scss" scoped>
.google-font-picker-toolbar {
  position: sticky;
  top: -1rem;
  background: white;
  z-index: 10;
  padding: 1rem 0;
  margin: -1rem 0 0 0;
}

.google-font-tabs {
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 0.5rem;
}

.preview-text-section {
  background: #f8f9fa;
  padding: 0.75rem;
  border-radius: 6px;
}

.google-fonts-grid-container {
  min-height: 300px;
}

.google-fonts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}

.font-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;

  &:hover {
    border-color: var(--primary-color, #4caf50);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &.font-card-selected {
    border-color: var(--primary-color, #4caf50);
    background: rgba(76, 175, 80, 0.05);
  }
}

.font-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.font-name {
  font-weight: 600;
  font-size: 0.9rem;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
}

.font-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.favorite-btn {
  color: #ccc;

  &.favorite-active {
    color: #ffc107;
  }

  &:hover {
    color: #ffc107;
  }
}

.email-safe-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #e3f2fd;
  color: #1976d2;
  border-radius: 50%;
  font-size: 0.7rem;
}

.font-preview {
  font-size: 1.5rem;
  line-height: 1.4;
  min-height: 60px;
  display: flex;
  align-items: center;
  color: #333;
  word-break: break-word;
  overflow: hidden;
}

.font-category {
  font-size: 0.75rem;
  color: #888;
  text-transform: capitalize;
  margin-top: 0.5rem;
}

// Responsive adjustments
@media (max-width: 768px) {
  .google-fonts-grid {
    grid-template-columns: 1fr;
  }

  .google-font-tabs .flex {
    flex-wrap: wrap;
  }
}
</style>
